import { baseApi } from "./api";

export const projectTicketApi = baseApi.injectEndpoints({
	endpoints: (build) => ({
		getProjectTickets: build.query({
			query: ({
				projectId,
				page = 1,
				limit = 50,
				search,
				ticketType,
				status,
				categoryId,
			}) => {
				const params = { limit, offset: (page - 1) * limit };
				if (search) params.search = search;
				if (ticketType) params.ticketType = ticketType;
				if (status) params.status = status;
				if (categoryId) params.categoryId = categoryId;
				return {
					url: `projects/${projectId}/tickets`,
					params,
				};
			},
			// Same cache entry across pages - items accumulate.
			serializeQueryArgs: ({ queryArgs }) => {
				const { projectId, search, ticketType, status, categoryId } =
					queryArgs;
				return { projectId, search, ticketType, status, categoryId };
			},
			merge: (currentCache, incoming, { arg }) => {
				if (arg.page === 1 || !currentCache) {
					return incoming;
				}
				return {
					...incoming,
					items: [
						...(currentCache.items || []),
						...(incoming.items || []),
					],
				};
			},
			forceRefetch: ({ currentArg, previousArg }) => {
				if (!previousArg) return true;
				return (
					currentArg?.page !== previousArg?.page ||
					currentArg?.search !== previousArg?.search ||
					currentArg?.ticketType !== previousArg?.ticketType ||
					currentArg?.status !== previousArg?.status ||
					currentArg?.categoryId !== previousArg?.categoryId
				);
			},
			providesTags: (result) => [
				{ type: "Ticket", id: "LIST" },
				...((result?.items ?? []).map((t) => ({
					type: "Ticket",
					id: t.id,
				}))),
			],
		}),
		getCategoryTickets: build.query({
			query: ({ projectId, categoryId, ...params }) => {
				const queryParams = Object.keys(params).length ? params : undefined;
				return {
					url: `projects/${projectId}/ticket-categories/${categoryId}/tickets`,
					params: queryParams,
				};
			},
			providesTags: (_r, _e, { categoryId }) => [
				{ type: "Ticket", id: `cat-${categoryId}` },
			],
		}),
		getProjectTicketStats: build.query({
			query: (projectId) => ({
				url: `projects/${projectId}/tickets/stats`,
			}),
			providesTags: (_r, _e, projectId) => [
				{ type: "Ticket", id: `stats-${projectId}` },
			],
		}),
		getTicketById: build.query({
			query: ({ projectId, ticketId }) => ({
				url: `projects/${projectId}/tickets/${ticketId}`,
			}),
			providesTags: (_r, _e, { ticketId }) => [
				{ type: "Ticket", id: ticketId },
			],
		}),
		createTicket: build.mutation({
			query: ({ projectId, categoryId, ...body }) => ({
				url: `projects/${projectId}/ticket-categories/${categoryId}/tickets`,
				method: "POST",
				body: { ...body, projectId },
			}),
			invalidatesTags: (_r, _e, { projectId, categoryId }) => [
				{ type: "Ticket", id: "LIST" },
				{ type: "Ticket", id: `cat-${categoryId}` },
				{ type: "Ticket", id: `stats-${projectId}` },
			],
		}),
		updateTicket: build.mutation({
			query: ({ projectId, ticketId, ...body }) => ({
				url: `projects/${projectId}/tickets/${ticketId}`,
				method: "PUT",
				body,
			}),
			invalidatesTags: (_r, _e, { projectId, ticketId }) => [
				{ type: "Ticket", id: ticketId },
				{ type: "Ticket", id: "LIST" },
				{ type: "Ticket", id: `stats-${projectId}` },
			],
		}),
		deleteTicket: build.mutation({
			query: ({ projectId, ticketId }) => ({
				url: `projects/${projectId}/tickets/${ticketId}`,
				method: "DELETE",
			}),
			invalidatesTags: (_r, _e, { projectId }) => [
				{ type: "Ticket", id: "LIST" },
				{ type: "Ticket", id: `stats-${projectId}` },
			],
		}),
		approveTicket: build.mutation({
			query: ({ projectId, ticketId }) => ({
				url: `projects/${projectId}/tickets/${ticketId}/approve`,
				method: "POST",
			}),
			invalidatesTags: (_r, _e, { projectId, ticketId }) => [
				{ type: "Ticket", id: ticketId },
				{ type: "Ticket", id: "LIST" },
				{ type: "Ticket", id: `stats-${projectId}` },
			],
		}),
	}),
});

export const {
	useGetProjectTicketsQuery,
	useGetCategoryTicketsQuery,
	useGetProjectTicketStatsQuery,
	useGetTicketByIdQuery,
	useCreateTicketMutation,
	useUpdateTicketMutation,
	useDeleteTicketMutation,
	useApproveTicketMutation,
} = projectTicketApi;
